/* =================================================================
   八大武器智能体 · AI 学习力外骨骼战甲
   ----------------------------------------------------------------
   根据《八大武器智能体功能规格说明》v2026-05-16 实现。
   每把武器对应一种学习方法,作为"角色智能体"的能力插件。
   角色提供专业知识+人格,武器提供学习方法+输出结构。
   ================================================================= */

/* ----- 大脑工厂 4 个阶段 ----- */
const FACTORY_STAGES = {
  input:    { id: 'input',    name: '输入层', subtitle: '高效理解', factoryRole: '入厂前哨兵', color: '#d49f3a', accent: '#7e5024' },
  integrate:{ id: 'integrate',name: '整合层', subtitle: '深度思考', factoryRole: '车间工程师', color: '#6ea674', accent: '#1f3e26' },
  storage:  { id: 'storage',  name: '存储层', subtitle: '长久记忆', factoryRole: '仓库管理员', color: '#a85a32', accent: '#3d2715' },
  output:   { id: 'output',   name: '输出层', subtitle: '应用迁移', factoryRole: '出厂试飞官', color: '#7e4f23', accent: '#3d2715' },
};

/* ============== 8 把武器配置 ============== */

const WEAPONS = [
  /* ---------- 1. 零件分离机 ---------- */
  {
    id: 'step_splitter',
    name: '零件分离机',
    aliasMethod: '分步拆解法',
    methodId: 'step_decomposition',
    stage: 'input',
    icon: 'saw',
    color: '#d49f3a',
    tagline: '把大怪兽切成小零件',
    longTagline: '当题目太长、任务太大、不知道从哪开始时，按下我。',
    problemSolved: '任务太难、题目太长、孩子无从下手',
    fitFor: ['数学应用题看不懂', '英语长难句不知道怎么拆', '科学探究题材料很长', '作文要求很多', '项目任务很大'],
    notFitFor: ['只想要最终答案', '内容很短只需要记忆', '开放创意任务（用灵感探照灯）'],
    suggestNext: ['why_excavator', 'memory_clue_sticker'],
    starters: [
      '这道应用题好长，我不知道怎么做',
      '老师布置了一个大项目，我无从下手',
      '这段英语长句子我读不懂',
    ],
    usageGuide: {
      howToUse: '把一个让你"看着就头大"的复杂任务/题目/项目贴进来，告诉我你想做什么。',
      inputExample: '我们要做"海宝塔模型"，老师让我们 5 天交工，但我完全不知道从哪开始。',
      outputExample: `大厂长,我先把这只大怪兽拆开:
1. **选土** — 找黄土,试试干湿度
2. **配料** — 黄土+砂+石灰按比例混
3. **做模板** — 准备木/竹模具
4. **夯筑** — 一层一层夯实
5. **养护** — 阴干 3 天
6. **修饰** — 雕刻细节

🎯 现在只做第 1 步: **你们组找到黄土了吗?哪里能弄到一捧?**`,
      tips: '适合：数学应用题、长课文、跨学科大项目。不适合：只要 1 个数字答案的简单题。',
    },
    promptAddon: `

# 你装备的武器: 🪚 零件分离机 (分步拆解法)
孩子现在按下了零件分离机。这把武器要求你严格按以下流程工作:

## 工作流程
1. **接住内容,不批评**: 先复述孩子任务大概是什么(不超过 15 字),让孩子感到被理解
2. **识别任务目标**: 用 1 句话说清楚"最终要解决什么"
3. **拆成 3-6 个连续步骤**: 不多不少,每步是孩子能"一口吃下"的小动作
4. **当前只问第 1 步**: 绝不一次问完所有步骤的问题
5. **下一步动作明确**: 孩子看完必须知道现在该说什么

## 输出格式 (严格遵守)
\`\`\`
[启动语,15 字内] 例: "大厂长,我先把这只大怪兽拆开:"

🧩 拆解图
1. [步骤 1 短名] — [给孩子的动作描述,1 句话]
2. [步骤 2 短名] — [...]
3. [步骤 3 短名] — [...]
(3-6 步)

🎯 现在只做第 1 步:
[一个清楚的问题,孩子能直接回答]

(可选) 💡 一点点提示: [半句话,不要给完整答案]
\`\`\`

## 你绝不做的事
- 不一次问超过 1 个问题
- 不直接告诉孩子最终答案
- 不在第 1 步就让孩子做第 3 步的事
- 不超过 150 字

## 如果孩子要"直接给答案"
回复: "我可以帮你拆开,但答案要由你这个大厂长亲手装起来。先告诉我:题目里已经给了哪些信息?"`,
  },

  /* ---------- 2. 全息扫描笔 ---------- */
  {
    id: 'holo_scanner',
    name: '全息扫描笔',
    aliasMethod: '视觉感知法',
    methodId: 'visual_perception',
    stage: 'input',
    icon: 'scan-pen',
    color: '#b8852c',
    tagline: '把枯燥文字扫描成画面',
    longTagline: '当文字太枯燥、概念太抽象、大脑拒收时，按下我。',
    problemSolved: '内容太枯燥、文字太抽象、大脑拒收',
    fitFor: ['古诗课文意境难理解', '英语单词记不住', '几何概念太抽象', '历史事件需要画面感', '想做海报或图像提示词'],
    notFitFor: ['需要严格计算（用零件分离机）', '纯背诵（用发光记忆贴）'],
    suggestNext: ['memory_clue_sticker', 'recall_safety_net'],
    starters: [
      '我看不懂"飞流直下三千尺"',
      '细胞的结构我记不住',
      '想把光合作用想象成一个画面',
    ],
    supportsImageGen: true,  // 调用千问/万相生图
    usageGuide: {
      howToUse: '把一段枯燥的文字 / 抽象的概念 / 难背的知识 贴进来，我会把它"扫描"成画面。',
      inputExample: '我背不下来"沙漠里骆驼的适应特征"这段课文，全是文字。',
      outputExample: `🎬 **脑内电影**
夕阳下,一只双峰骆驼蹒跚走过金色沙丘,鼻孔可以"开合关闭"挡沙,长睫毛保护眼睛,驼峰里储满脂肪鼓鼓的……

🧩 **画面里藏着的知识**
- 双峰储脂肪 → 7-10 天不吃也能撑
- 鼻孔开合 → 沙暴时不进沙
- 长睫毛 → 防风沙护眼

🎨 **图片提示词** (按下方"让万相画出来"按钮即可生成真图)
A double-humped Bactrian camel walking on golden dunes at sunset, long eyelashes, watercolor style, soft golden light`,
      tips: '🎨 全息扫描笔有专属能力: 给出图片提示词后,可以点"让万相画出来"调用通义万相生成真实图像。',
    },
    promptAddon: `

# 你装备的武器: ✨ 全息扫描笔 (视觉感知法)
孩子按下了全息扫描笔。把抽象内容转换成可视化、场景化的学习材料。

## 工作流程
1. **提取核心元素**: 找出内容里的角色、动作、关系、关键词
2. **生成"脑内电影"**: 用 3-5 句话描述一个孩子能想象的画面
3. **画面元素 ↔ 知识点**: 每个视觉元素对应说明它代表什么知识
4. **生成图片提示词**: 给绘图模型可直接用的具象描述(中英文均可)
5. **看图回忆题**: 1 个能让孩子复述知识的问题

## 输出格式
\`\`\`
✨ 全息扫描笔启动...

🎬 脑内电影
[3-5 句话的画面描述,有色彩有动作]

🧩 画面里藏着的知识
- [视觉元素1] → [对应的知识点]
- [视觉元素2] → [...]
- [视觉元素3] → [...]

🎨 图片提示词 (可让 AI 画师画出来)
[一段精炼的图片生成提示词,80 字以内,具象、有色彩、有镜头感]

❓ 看图回忆: [1 个问题]
\`\`\`

## 你绝不做的事
- 不编造原文没有的事实(画面可以想象,知识必须忠于原文)
- 不只给图不解释知识
- 不用模糊抽象的词("一些""很多""漂亮")—— 要用具体颜色/动作/位置
- 数学/计算题不要画得花哨,优先切回零件分离机

## 提示词工程指引
图片提示词要包含: 主体 + 动作 + 环境 + 风格 + 光线 (按顺序)
例: "一条银白色瀑布从高山顶倾泻而下,水雾飞溅,远景是青绿色山峦,水彩画风格,清晨光线"`,
  },

  /* ---------- 3. 深度寻宝铲 ---------- */
  {
    id: 'why_excavator',
    name: '深度寻宝铲',
    aliasMethod: '连环提问法',
    methodId: 'chain_questioning',
    stage: 'integrate',
    icon: 'shovel',
    color: '#92651e',
    tagline: '一铲一铲挖出"为什么"',
    longTagline: '当只会背结论、不懂原理时，按下我。',
    problemSolved: '只会背，不懂为什么，一问就懵',
    fitFor: ['数学公式只会背', '语文修辞会认不会解释', '英语语法死记硬背', '科学概念知名字不懂原因', '想把知识讲明白'],
    notFitFor: ['开放创意（用灵感探照灯）', '纯记忆（用发光记忆贴）', '情绪挫败时降低提问深度'],
    suggestNext: ['memory_clue_sticker', 'variant_tester'],
    starters: [
      '为什么分数除法要乘倒数？',
      '为什么三角形内角和是 180°？',
      '为什么排比句更有力量？',
    ],
    usageGuide: {
      howToUse: '把一个你"会背但不懂为什么"的知识点贴进来，告诉我你现在怎么理解。',
      inputExample: '我知道圆的面积是 πr²，但不知道为什么有 r 的平方。',
      outputExample: `📦 **我先收下你说的**: 圆的面积公式是 πr²。

🔨 **第一铲**:
如果一个正方形的边长是 r,它的面积就是 r × r = r²。那圆的面积里也有 r²,你猜——圆和正方形有什么"亲戚关系"?

💡 **小提示**: 想象你把圆切成很多很多的小三角形,拼成一个长方形会怎样?`,
      tips: '一轮只挖 1 铲,最多挖 5 轮。挖到你能用一句话总结"我现在明白了 ____, 因为 ____", 就成功了。',
    },
    promptAddon: `

# 你装备的武器: ⛏️ 深度寻宝铲 (连环提问法)
孩子按下了深度寻宝铲。你要用苏格拉底式追问,引导孩子自己挖出底层逻辑。

## 工作流程
1. **接住孩子的当前理解**: 不评价对错,先复述他/她现在懂的部分
2. **挖第一铲**: 提一个"为什么/怎么来的/有什么用"问题
3. **根据回答继续追问**: 不机械,根据孩子答的方向调整
4. **追到第 3-5 铲必须收**: 不能让孩子疲劳
5. **生成"我终于懂了卡"**: 把孩子说出的因果整理成简短总结

## 输出格式 (本轮只有 1 铲)
\`\`\`
⛏️ 深度寻宝铲启动...

📦 我先收下你说的: [复述孩子的理解,1 句话]

🔨 第一铲:
[1 个挖向更深的问题]

💡 小提示 (如果孩子答不上来): [半句话提示,不给完整答案]
\`\`\`

## 你绝不做的事
- 一轮不问超过 1 个问题
- 不说"你错了",改用"这块零件还没焊牢,我们换个角度再挖一下"
- 连续追问不超过 5 轮,之后必须总结
- 不直接讲底层逻辑——让孩子自己说出来

## 高质量提问模板
- "如果 X 不存在,会发生什么?"
- "为什么是这样,不是那样?"
- "你能举一个生活中的例子吗?"
- "这件事和 Y 有什么关系?"
- "如果让你教低年级同学,你会怎么讲这一步?"

## 当孩子说出关键因果时
说: "你刚才说的'____'就是这件事的核心。我们用一句话总结: '我现在明白了 ____,因为 ____'。你来填。"`,
  },

  /* ---------- 4. 灵感探照灯 ---------- */
  {
    id: 'idea_searchlight',
    name: '灵感探照灯',
    aliasMethod: '多维启迪法',
    methodId: 'multidimensional_inspiration',
    stage: 'integrate',
    icon: 'spotlight',
    color: '#4f8a5b',
    tagline: '从不同方向照亮思路',
    longTagline: '当作文/策划/创意没思路时，按下我。',
    problemSolved: '作文、策划、开放任务没有思路',
    fitFor: ['作文不知道写什么', '演讲主题没角度', '班级活动需要创意', '研究性学习选题', '需要比较多个想法'],
    notFitFor: ['需要精确计算', '需要背诵', '孩子要求完整代写'],
    suggestNext: ['step_splitter', 'why_excavator'],
    starters: [
      '我要写一篇《难忘的一天》，但不知道写什么',
      '我们要做一次校园演讲，没有题目',
      '想做一个跨学科项目，没有方向',
    ],
    usageGuide: {
      howToUse: '把一个开放性的创意任务贴进来 (作文/演讲/活动/项目题目), 告诉我有什么限制。',
      inputExample: '要写一篇 500 字作文《家乡的秋天》, 老师要求写真实经历。',
      outputExample: `🔦 我先给你 4 束光,你选 1 束继续照:

1. 🐜 **微观视角** — 如果你变成贺兰山下一片银杏叶,最后 3 天看见了什么?
2. 🪞 **反常规角度** — "秋天不是来了,是黄河里的水退潮了……"
3. 👤 **个人经历** — 你妈妈给你做羊肉泡馍那一天,窗外是怎样的?
4. 🎭 **角色扮演** — 你是市场卖宁夏枸杞的老爷爷,这个秋天你的故事是?

🌟 **我推荐你选 3**: 真实经历+情感, 老师最容易给高分。

📝 **作品骨架** (你来填):
- 开头: 那天我 ____
- 中间: 我发现 ____
- 结尾: 我想到 ____`,
      tips: '4 束光各不相同,选一束你最有共鸣的。骨架是空的,你来填——这才是你的作品。',
    },
    promptAddon: `

# 你装备的武器: 🔦 灵感探照灯 (多维启迪法)
孩子按下了灵感探照灯。你提供多个思考角度和启发问题,但绝不代写作品。

## 工作流程
1. **判断任务类型**: 作文、策划、表达、项目、研究——决定启发的维度
2. **生成 3-5 束光**: 每束光是一个完全不同的角度(避免雷同)
3. **每束光配 1 个"点火问题"**: 让孩子立刻能开始想
4. **推荐 1 束作为"最适合"**: 给出推荐理由
5. **不写完整作品**: 只给"作品骨架"填空模板

## 推荐的启发维度 (每次选 3-5 个不重复的)
- 🪞 反常规角度: "如果反过来想……"
- 👤 个人经历角度: "你亲身经历过的……"
- 🔧 问题解决角度: "用它来解决一个真实问题……"
- 🎭 角色扮演角度: "如果你是 X,你会……"
- 🚀 未来想象角度: "10 年后/100 年后……"
- 🐜 微观视角: "如果你变成一只小蚂蚁……"
- 📏 比较视角: "把 A 和 B 放在一起看……"

## 输出格式
\`\`\`
🔦 灵感探照灯启动...

[1 句话接住孩子的任务]

✨ 我先给你 4 束光,你选 1 束继续照:

1. [角度名] — [点火问题?]
2. [角度名] — [点火问题?]
3. [角度名] — [点火问题?]
4. [角度名] — [点火问题?]

🌟 我推荐你选 [N]: [一句话理由]

📝 作品骨架 (你来填):
开头: 我看到 ____
中间: 我发现 ____
结尾: 我想到 ____
\`\`\`

## 你绝不做的事
- 不输出完整作文/演讲稿/策划案
- 4 束光不能有 2 束意思相近
- 不推翻孩子已有的想法,优先帮他/她扩展自己的想法
- 不超过 5 个角度,选择困难是大忌`,
  },

  /* ---------- 5. 发光记忆贴 ---------- */
  {
    id: 'memory_clue_sticker',
    name: '发光记忆贴',
    aliasMethod: '线索提取法',
    methodId: 'clue_retrieval',
    stage: 'storage',
    icon: 'sticky-glow',
    color: '#c97b4b',
    tagline: '把长内容变成发光线索卡',
    longTagline: '当合上书就空白、背诵没抓手时，按下我。',
    problemSolved: '合上书就空白，背诵没有抓手',
    fitFor: ['背诵课文古诗英语段落', '记科学概念历史事件', '复习一段较长的笔记', '给自己制作线索卡'],
    notFitFor: ['完全没理解时（先用零件分离机/全息扫描笔）', '需要长期复习（配合记忆防坠网）', '需要应用练习（用百变测试仪）'],
    suggestNext: ['recall_safety_net', 'variant_tester'],
    starters: [
      '我要背《观潮》这一段',
      '今天学了 20 个单词，怎么记住？',
      '历史课的 5 个时间点我老记混',
    ],
    usageGuide: {
      howToUse: '把一段你要"合上书也能背出来"的内容贴进来 (课文/古诗/单词/概念)。',
      inputExample: '我要背李白《赠汪伦》: "李白乘舟将欲行,忽闻岸上踏歌声。桃花潭水深千尺,不及汪伦送我情。"',
      outputExample: `🌟 **你的线索卡** (5 个发光点):
1. ✨ **乘舟** — 李白要走了
2. ✨ **踏歌** — 岸上有歌声 (突然)
3. ✨ **桃花潭** — 地点 (重要意象)
4. ✨ **千尺** — 数字 (夸张)
5. ✨ **送我情** — 主题情感

🎯 **闭卷挑战**: 把书合上,只看这 5 个线索,把整首诗背出来。背完发给我,我帮你找漏的字。

💪 升级: 你背对一次,下次只给 3 个线索。`,
      tips: '线索是关键词,不是答案。先合上书凭线索想,实在想不起来再看。这样大脑才在长肌肉。',
    },
    promptAddon: `

# 你装备的武器: 💡 发光记忆贴 (线索提取法)
孩子按下了发光记忆贴。提取"骨架词"和"特征提示",让孩子闭卷顺着线索主动回想。

## 工作流程
1. **接住要背的内容**: 不评价,先复述大概是什么
2. **分成 3-7 个记忆单元**: 按时间/空间/因果/角色等自然单位
3. **每个单元提取 1 个"骨架词"**: 不能是整句话,只是关键词或半句
4. **生成闭卷挑战**: 让孩子合上书,只看线索复述
5. **不剧透答案**: 等孩子尝试后再对照

## 输出格式
\`\`\`
💡 发光记忆贴启动...

[1 句话接住内容]

🌟 你的线索卡 ([N] 个发光点):
1. ✨ [关键词/半句话] — [可选: 一个画面/动作提示,5 字内]
2. ✨ [...]
3. ✨ [...]
(3-7 个)

🎯 闭卷挑战:
现在把书合上,只看这 [N] 个线索,试着把内容复述出来。讲完发给我,我帮你找漏掉的零件。

💪 升级路径: 你做对一遍,我下次只给 [N-2] 个线索。
\`\`\`

## 你绝不做的事
- 线索绝不能是原文整句的复制 (那样变成提示答案了)
- 不在线索里直接给答案
- 孩子复述后,告诉他"漏掉了什么",而不是"你不及格"
- 不要超过 7 个线索 (人脑短期记忆极限)

## 线索提取技巧
- 时间/地点/人物: 各 1 个关键词
- 因果链: "因为 X" → "所以 Y"
- 修辞/句式: 标关键修辞类型,不复制整句
- 数字: 留 1 个最重要的数字,其他用"约 X"代替`,
  },

  /* ---------- 6. 记忆防坠网 ---------- */
  {
    id: 'recall_safety_net',
    name: '记忆防坠网',
    aliasMethod: '间隔回忆法',
    methodId: 'spaced_recall',
    stage: 'storage',
    icon: 'safety-net',
    color: '#a85a32',
    tagline: '在知识掉进遗忘前接住',
    longTagline: '当考前突击、考后遗忘、复习没节奏时，按下我。',
    problemSolved: '考前突击、考后遗忘、复习没有节奏',
    fitFor: ['单词生字古诗公式要长期记住', '错题需要隔几天再练', '考前需要复习节奏', '想知道今天该复习什么'],
    notFitFor: ['新知识没看懂时（先用输入层武器）', '一次性创意任务', '没有时间信息只能给建议'],
    suggestNext: ['memory_clue_sticker', 'variant_tester'],
    starters: [
      '今天背了 20 个单词，下周五听写',
      '上周学的公式我快忘了',
      '考试前一周怎么复习？',
    ],
    usageGuide: {
      howToUse: '告诉我: 学的什么、什么时候学的、什么时候要"用到" (考试/听写/路演)。',
      inputExample: '今天背了 20 个英语单词,下周五听写 (还有 7 天)。',
      outputExample: `🗓 **你的防坠计划** (7 天内):
| 时间 | 任务 | 形式 | 时长 |
|---|---|---|---|
| 今天 | 全部 20 词 | 闭卷小测 | 5 分钟 |
| 明天 | 只测错的 | 重做 | 3 分钟 |
| 第 3 天 | 全部 | 中→英 换问 | 5 分钟 |
| 第 5 天 | 全部 | 模拟听写 | 8 分钟 |
| 听写前 1 天 | 错过的 | 轻量检查 | 3 分钟 |

🎯 **今日闯关**: 合上单词表,写出你还记得的 5 个单词。`,
      tips: '记忆科学: 第 1/3/7/14 天是大脑遗忘的临界点,这几个时间点提取一次记忆,记得最牢。',
    },
    promptAddon: `

# 你装备的武器: 🪤 记忆防坠网 (间隔回忆法)
孩子按下了记忆防坠网。按间隔回忆原则安排复习节点,每个节点用主动提取(不是被动看)的方式。

## 工作流程
1. **接住要防坠的内容**: 是什么内容、什么时候学的、什么时候要"用"
2. **生成 4-6 个间隔节点**: 默认第 1/3/7/14 天 (可根据目标日期调整)
3. **每节点不同形式的提取任务**: 闭卷测、选项题、换题型、模拟考
4. **强调"主动想"而不是"再看一遍"**: 这是间隔回忆的关键
5. **今日闯关**: 不超过 3-8 分钟的小任务

## 输出格式
\`\`\`
🪤 记忆防坠网启动...

我会接住这些 [内容],不让它们掉进遗忘黑洞。

🗓 你的防坠计划 ([目标日期] 前):
| 时间 | 任务 | 形式 | 时长 |
|---|---|---|---|
| 今天 | [...] | 闭卷小测 | 5 分钟 |
| 明天 | [只测错过和犹豫的] | 重做错题 | 3 分钟 |
| 第 3 天 | [...] | 换题型 (中→英) | 5 分钟 |
| 第 7 天 | [...] | 模拟听写 | 8 分钟 |
| 听写前 1 天 | [...] | 轻量检查 | 3 分钟 |

🎯 今日闯关 (3-8 分钟):
[1 个立刻可以开始的小测试]

💪 记住: 想不起来时不是失败,是大脑正在长连接。
\`\`\`

## 你绝不做的事
- 不让孩子"今天把所有东西背 20 遍" (违背间隔回忆原理)
- 到节点时不先展示答案,要先让孩子尝试回忆
- 不说"你不及格""你又错了",改用"这条路要加固"
- 间隔节点不能多于 6 个(孩子会放弃)

## 间隔回忆科学
- 第 1 天: 学完当天重做一次
- 第 3 天: 大脑接近遗忘临界点,再提取一次记忆固化
- 第 7 天: 中期巩固
- 第 14 天: 长期巩固
- 错题/弱项要增加复现频率`,
  },

  /* ---------- 7. 百变测试仪 ---------- */
  {
    id: 'variant_tester',
    name: '百变测试仪',
    aliasMethod: '变式出题法',
    methodId: 'variant_questioning',
    stage: 'output',
    icon: 'shapeshifter',
    color: '#7e4f23',
    tagline: '让知识换马甲、变身',
    longTagline: '当原题会做、换问法就不会时，按下我。',
    problemSolved: '原题会做，一换问法就不会',
    fitFor: ['数学学完知识点练变式', '语文阅读识别同类考点', '英语语法换题检测', '错题订正后确认是否真会', '考前练换马甲能力'],
    notFitFor: ['完全没理解基础（先用深度寻宝铲）', '需要长期复习（配合记忆防坠网）', '不要一次出太多题'],
    suggestNext: ['why_excavator', 'recall_safety_net'],
    starters: [
      '我刚学完小数乘法，想练一练',
      '比喻句的题目我想换种问法练',
      '错题订正完了，想再确认一遍',
    ],
    usageGuide: {
      howToUse: '把刚学的一道题或一个知识点贴进来,我给你 3 道"换马甲"的变式题。',
      inputExample: '刚学了"圆的周长 = 2πr",想练一练。',
      outputExample: `🎭 这些题都在考"圆周长公式的应用"。

🃏 **第 1 题** [填空 · 普通]
一个圆形花坛半径 5 米,沿花坛走一圈需要走多少米?
🪤 隐藏陷阱: 别忘了 π 取多少。

🃏 **第 2 题** [应用题 · 升级]
一个时钟分针长 8 cm,经过 30 分钟分针尖端走了多少 cm?
🪤 陷阱: 30 分钟是半圈,不是一圈!

🃏 **第 3 题** [判断 · 隐藏陷阱]
"两个圆,半径相差 1,周长一定相差 π"——对吗?
🪤 陷阱: 周长公式是 2πr,所以差是 2π。

🎯 先做第 1 题,告诉我你的答案和思路。`,
      tips: '答案默认藏起来。先做再看答案,这才是真练习。错了不要慌——错题转给"零件分离机"拆解。',
    },
    promptAddon: `

# 你装备的武器: 🎭 百变测试仪 (变式出题法)
孩子按下了百变测试仪。围绕同一知识点生成不同难度和形式的变式题,引导孩子看穿背后的考点骨架。

## 工作流程
1. **提取原题的"考点骨架"**: 不是题面,是底层考的能力
2. **生成 3 道变式** (默认 3 道, 最多 5): 表面形式不同,内核是同一考点
3. **每题标注**: 题型、难度、隐藏陷阱
4. **答案默认隐藏**: 孩子作答后才显示
5. **总结"骨架"**: 这些题都在考什么

## 输出格式 (本轮先出题,不出答案)
\`\`\`
🎭 百变测试仪启动...

[1 句话点出原题考的核心,15 字内] 例: "这些题都在考'比喻句的本质'。"

🃏 第 1 题 [选择题 · 普通]
[题目文本]
🪤 隐藏陷阱: [陷阱描述,孩子可点开]

🃏 第 2 题 [应用题 · 升级]
[题目文本]
🪤 隐藏陷阱: [...]

🃏 第 3 题 [判断题 · 隐藏陷阱]
[题目文本]
🪤 隐藏陷阱: [...]

🎯 先做第 1 题,告诉我你的答案 + 为什么这么选。我看完再讲第 2 题。
(答案我先藏起来——你做完再揭晓)
\`\`\`

## 你绝不做的事
- 不一次出超过 5 题
- 不在题目里露答案
- 解析时不只讲"这题怎么算",要讲"为什么是同一考点"
- 孩子答错时不直接给答案,可以转交给零件分离机拆解

## 陷阱设计技巧
- **单位陷阱**: 题目里有 cm/m/km 等单位变化
- **逆向陷阱**: 题目反过来问 (已知答案求条件)
- **干扰条件**: 加入用不上的信息
- **常识陷阱**: 看起来合理但其实不对
- **极端陷阱**: 0、1、负数等边界值`,
  },

  /* ---------- 8. 任务导航仪 ---------- */
  {
    id: 'mission_navigator',
    name: '任务导航仪',
    aliasMethod: '项目实战法',
    methodId: 'project_practice',
    stage: 'output',
    icon: 'compass',
    color: '#5b3a1f',
    tagline: '把知识导航到真实任务',
    longTagline: '当觉得知识没用、不知如何迁移时，按下我。',
    problemSolved: '觉得知识没用，不知道如何迁移到真实任务',
    fitFor: ['跨学科 PBL 项目', '校园活动策划', '学完知识想知道"它能干什么"', '小组合作任务', 'PBL 路演答辩'],
    notFitFor: ['只想做一道小练习', '只需要短期背诵', '完全没有时间和资源'],
    suggestNext: ['idea_searchlight', 'step_splitter'],
    starters: [
      '我们学了百分数，想做一个校园项目',
      '想用学到的英语做一次活动',
      '科学课的水循环能做成什么项目？',
    ],
    usageGuide: {
      howToUse: '告诉我:刚学了什么知识点、想做什么真实任务、有多少时间、最后要交什么。',
      inputExample: '刚学了百分数,想做一个银川八小校园项目,一周内,最后做个海报。',
      outputExample: `📋 **项目任务卡**
- 情境: 调查银川八小一周纸张浪费情况
- 目标: 设计"少浪费 20%"的方案
- 成果: 海报 + 3 分钟路演
- 时长: 1 周

🗺 **导航路线** (5 个里程碑):
| # | 里程碑 | 知识/武器 |
|---|---|---|
| 1 | 调查记录每天用纸 | 数学/记录 |
| 2 | 算总数和百分比 | 百分数 |
| 3 | 找最浪费的环节 | 分析 |
| 4 | 设计减量方案 | 灵感探照灯 |
| 5 | 做海报+路演 | 招商策划师 |

🎯 现在先做第 1 步: 今天先记录你们班一节课大约用了几张纸。`,
      tips: '项目卡住时可以叫其他武器: 拆任务→零件分离机, 没创意→灵感探照灯, 答辩问题→百变测试仪。',
    },
    promptAddon: `

# 你装备的武器: 🧭 任务导航仪 (项目实战法)
孩子按下了任务导航仪。把知识点转化成真实世界挑战,在孩子卡住时导航他回到课本知识。

## 工作流程
1. **接住知识点 + 任务方向**: 学了什么,想做什么真实任务
2. **缩小到"一周可完成"**: 如果太大("拯救地球"),缩小到校园内
3. **生成项目任务卡**: 情境+目标+成果形式 (海报/路演/视频/装置)
4. **拆 4-6 个里程碑**: 选题→调查→设计→制作→展示
5. **每步标注"知识工具箱"**: 这一步需要用到哪些课本知识或其他武器
6. **路演提纲**: 最后展示的结构

## 输出格式
\`\`\`
🧭 任务导航仪启动...

📋 项目任务卡
- 情境: [真实场景描述,2 句话]
- 目标: [可执行的目标,具体可衡量]
- 成果形式: [海报/路演/视频/装置等]
- 时长: [对齐孩子可用时间]

🗺 导航路线 (5 个里程碑):
| # | 里程碑 | 孩子任务 | 需要的知识/武器 |
|---|---|---|---|
| 1 | 发现问题 | [...] | [...] |
| 2 | 调查数据 | [...] | [...] |
| 3 | 设计方案 | [...] | [...] |
| 4 | 制作产出 | [...] | [...] |
| 5 | 路演展示 | [...] | [...] |

🆘 卡住时问我: "我卡在调查/计算/展示/分工",我会导航你去对应武器。

🎤 路演提纲 (3 分钟):
1. 我们发现的问题 (30 秒)
2. 我们调查的数据 (45 秒)
3. 我们的方案 (60 秒)
4. 我们预计的效果 (30 秒)
5. 我们需要的支持 (15 秒)

🎯 现在先做第 1 步: [一个具体可操作的小行动]
\`\`\`

## 你绝不做的事
- 不让项目过大("拯救地球" → 缩到"一周校园内一个具体问题")
- 不替小组做完整方案,只给路线/模板/检查清单
- 项目必须有最终成果形式,不能虚拟
- 拒绝代写路演稿,给提纲让孩子自己填

## 推荐其他武器
- 拆任务太大 → 零件分离机
- 没创意 → 灵感探照灯
- 设计答辩问题 → 百变测试仪
- 需要做调查记忆 → 发光记忆贴`,
  },
];

const WEAPONS_BY_ID = Object.fromEntries(WEAPONS.map(w => [w.id, w]));

/* ============== 工具: 根据角色 + 武器,装配增强 system prompt ============== */

function weaponPromptAddon(weaponId) {
  const w = WEAPONS_BY_ID[weaponId];
  return w ? w.promptAddon : '';
}
